import styles from "@/asserts/styles/formSection.module.less";
import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import { budgetTypeOptions, CURRENCY_OPTIONS } from "@/constants";
import { getExchangeRate } from "@/apis/sap.api";
import { Col, Form, Input, InputNumber, Row, Select, Space } from "antd";
import type { HighValuePurchaseRequisitionFormValue } from "../../type";
import { useEffect, useState } from "react";
import { useWatch } from "antd/es/form/Form";
import Big from "big.js";
import { HkdPriceLabel } from "@/components/ui/HkdPriceLabel/HkdPriceLabel";
import { useTranslation } from "react-i18next";

const { TextArea } = Input;

export const BudgetInfoFormFields = () => {
  const { t } = useTranslation("purchaseRequestManagement");
  const form = Form.useFormInstance<HighValuePurchaseRequisitionFormValue>();
  const budgetType = useWatch("budgetType", form);
  const budgetAmt = useWatch("budgetAmt", form);
  const budgetAmtUnit = useWatch("budgetAmtUnit", form);
  const totalBudgetAmt = useWatch("totalBudgetAmt", form);
  const totalBudgetAmtUnit = useWatch("totalBudgetAmtUnit", form);

  const [budgetAmtHkd, setBudgetAmtHkd] = useState<string | null>(null);
  const [totalBudgetAmtHkd, setTotalBudgetAmtHkd] = useState<string | null>(
    null,
  );

  // 计算预算金额 HKD
  useEffect(() => {
    const calculateBudgetHkd = async () => {
      if (budgetAmt && budgetAmtUnit && budgetAmtUnit !== "HKD") {
        try {
          const res = await getExchangeRate(budgetAmtUnit, "HKD");
          const rate = res.data.data.toString();
          const amountHkd = new Big(budgetAmt || 0)
            .times(new Big(rate || 0))
            .round(2)
            .toString();
          setBudgetAmtHkd(amountHkd);
          form.setFieldsValue({ budgetAmtHkd: amountHkd });
        } catch {
          setBudgetAmtHkd(null);
          form.setFieldsValue({ budgetAmtHkd: undefined });
        }
      } else if (budgetAmt && budgetAmtUnit === "HKD") {
        setBudgetAmtHkd(budgetAmt);
        form.setFieldsValue({ budgetAmtHkd: budgetAmt });
      } else {
        setBudgetAmtHkd(null);
        form.setFieldsValue({ budgetAmtHkd: undefined });
      }
    };
    calculateBudgetHkd();
  }, [budgetAmt, budgetAmtUnit, form]);

  // 计算申请总金额 HKD
  useEffect(() => {
    const calculateTotalBudgetHkd = async () => {
      if (
        totalBudgetAmt &&
        totalBudgetAmtUnit &&
        totalBudgetAmtUnit !== "HKD"
      ) {
        try {
          const res = await getExchangeRate(totalBudgetAmtUnit, "HKD");
          const rate = res.data.data.toString();
          const amountHkd = new Big(totalBudgetAmt || 0)
            .times(new Big(rate || 0))
            .round(2)
            .toString();
          setTotalBudgetAmtHkd(amountHkd);
          form.setFieldsValue({ totalBudgetAmtHkd: amountHkd });
        } catch {
          setTotalBudgetAmtHkd(null);
          form.setFieldsValue({ totalBudgetAmtHkd: undefined });
        }
      } else if (totalBudgetAmt && totalBudgetAmtUnit === "HKD") {
        setTotalBudgetAmtHkd(totalBudgetAmt);
        form.setFieldsValue({ totalBudgetAmtHkd: totalBudgetAmt });
      } else {
        setTotalBudgetAmtHkd(null);
        form.setFieldsValue({ totalBudgetAmtHkd: undefined });
      }
    };
    calculateTotalBudgetHkd();
  }, [totalBudgetAmt, totalBudgetAmtUnit, form]);

  // 判断预算类型是否为 A、B、C
  const shouldShowBudgetAmt =
    budgetType === "A" || budgetType === "B" || budgetType === "C";

  return (
    <div className={styles.formSection}>
      <ModalSectionHeader title={t("预算信息")} />

      <div className={styles.sectionBody}>
        <Row gutter={49}>
          {/* 预算类型 必填 */}
          <Col span={8}>
            <Form.Item<HighValuePurchaseRequisitionFormValue>
              name="budgetType"
              label={t("已批准预算类型")}
              rules={[
                {
                  required: true,
                  message: t("请选择"),
                },
              ]}
            >
              <Select placeholder={t("请选择")} options={budgetTypeOptions} />
            </Form.Item>
          </Col>

          {/* 项目编号 条件显示 B、C */}
          <Form.Item noStyle shouldUpdate>
            {() => {
              const bt = form.getFieldValue("budgetType");
              const show = bt === "B" || bt === "C";

              if (!show) return null;
              return (
                <Col span={8}>
                  <Form.Item<HighValuePurchaseRequisitionFormValue>
                    name="projectNo"
                    label={t("项目编号")}
                    rules={[
                      {
                        required: show,
                        message: t("请输入"),
                      },
                    ]}
                  >
                    <Input placeholder={t("请输入")} />
                  </Form.Item>
                </Col>
              );
            }}
          </Form.Item>

          {/* 原因说明 条件显示 D */}
          <Form.Item noStyle shouldUpdate>
            {() => {
              const bt = form.getFieldValue("budgetType");
              const show = bt === "D";
              if (!show) return null;
              return (
                <Col span={8}>
                  <Form.Item<HighValuePurchaseRequisitionFormValue>
                    name="budgetRemarks"
                    label={t("原因说明")}
                    rules={[
                      {
                        required: show,
                        message: t("请输入"),
                      },
                    ]}
                  >
                    <TextArea rows={1} placeholder={t("请输入")} />
                  </Form.Item>
                </Col>
              );
            }}
          </Form.Item>

          {/* 预算金额 条件显示 A、B、C */}
          {shouldShowBudgetAmt && (
            <>
              <Col span={8}>
                <Form.Item label={t("预算金额/币种")} required>
                  <Space.Compact style={{ width: "100%" }}>
                    <Form.Item<HighValuePurchaseRequisitionFormValue>
                      name="budgetAmt"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: t("请输入预算金额"),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "60%" }}
                        placeholder={t("请输入")}
                        min={0}
                        precision={2}
                      />
                    </Form.Item>
                    <Form.Item<HighValuePurchaseRequisitionFormValue>
                      name="budgetAmtUnit"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: t("请选择币种"),
                        },
                      ]}
                    >
                      <Select
                        style={{ width: "40%" }}
                        options={CURRENCY_OPTIONS}
                        placeholder={t("请选择")}
                      />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
                <HkdPriceLabel priceHkd={budgetAmtHkd} />
              </Col>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="budgetAmtHkd"
                label={t("预算金额HKD")}
                hidden
              ></Form.Item>
            </>
          )}

          {/* 申请总金额 必填 */}
          <Col span={8}>
            <Form.Item label={t("申请总金额/币种")} required>
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item<HighValuePurchaseRequisitionFormValue>
                  name="totalBudgetAmt"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("请输入申请总金额"),
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "60%" }}
                    placeholder={t("请输入")}
                    min={0}
                    precision={2}
                  />
                </Form.Item>
                <Form.Item<HighValuePurchaseRequisitionFormValue>
                  name="totalBudgetAmtUnit"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("请选择币种"),
                    },
                  ]}
                >
                  <Select
                    style={{ width: "40%" }}
                    options={CURRENCY_OPTIONS}
                    placeholder={t("请选择")}
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <HkdPriceLabel priceHkd={totalBudgetAmtHkd} />
          </Col>
          <Form.Item<HighValuePurchaseRequisitionFormValue>
            name="totalBudgetAmtHkd"
            label={t("申请总金额HKD")}
            hidden
          ></Form.Item>
        </Row>
      </div>
    </div>
  );
};
